<template>
  <div class="searchable-select-wrapper relative" ref="wrapper">
    <input
      v-if="name"
      ref="vvInput"
      v-validate="rules"
      :name="name"
      :data-vv-as="dataVvAs || resolvedPlaceholder"
      :data-vv-scope="scope || false"
      :value="value || ''"
      type="text"
      class="hidden"
      tabindex="-1"
      readonly
      area-hidden="true"
    />

    <!--  Trigger  -->
    <button
      type="button"
      :disabled="disabled"
      @click="toggleOpen"
      :class="[
        { 'ring-1 ring-orange bg-gray-100 select-placeholder ': errors.has(name) },
        disabled ? 'cursor-default' : 'cursor-pointer',
        !value ? 'text-gray-900/20' : 'text-gray-900',
        'h-12 w-full flex items-center rounded-md bg-gray-100 font-semibold text-base px-4',
      ]"
    >
      <span :class="'flex-1 ltr:text-left rtl:text-right truncate'">
        {{ displayValue || resolvedPlaceholder }}
      </span>
      <img
        :class="{ 'rotate-180': isOpen }"
        class="absolute top-[18px] h-[13px] ltr:right-3 rtl:left-3 transform transition-transform duration-200 pointer-events-none"
        width="13px"
        :src="errors.has(name) ? require('../assets/img/dropdown-arrow-red.svg') : require('../assets/img/icons/upload/arrow-down.svg')"
        alt="arrow"
      />
    </button>

    <!--  Dropdown panel  -->
    <transition name="picker">
      <div
        v-if="isOpen"
        class="picker-panel absolute z-10 left-0 right-0 top-[calc(100%+5px)] w-full max-h-[314px] flex flex-col p-2 bg-gray-100 border rounded overflow-hidden"
        role="listbox"
        :aria-label="resolvedPlaceholder"
      >
        <!-- Search row -->
        <div class="relative flex items-center shrink-0 mb-1">
          <!-- Search icon -->
          <img class="absolute ltr:left-3 rtl:right-3 flex items-center w-3 h-3 bg-no-repeat pointer-events-none" width="13px" src="../assets/img/icons/competitions/search-icon.svg" alt="arrow" />
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            :placeholder="resolvedSearchPlaceholder"
            class="w-full h-8 px-8 rounded border border-[#2563eb] bg-white text-gray-900"
            autocomplete="off"
            @keydown.esc="closeDropdown"
            @keydown.enter.prevent="selectFirstFiltered"
            @keydown.down.prevent="focusNextOption(1)"
            @keydown.up.prevent="focusNextOption(-1)"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="absolute right-2 flex items-center text-primary-text"
            @click.stop="searchQuery = ''"
            tabindex="-1"
            aria-label="Clear search"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </button>
        </div>
        <!-- Options list -->
        <div class="picker-options-scroll overflow-y-auto" ref="optionsScroll">
          <template v-if="filteredOptions.length > 0">
            <div
              v-for="(option, index) in filteredOptions"
              :key="getTrackByValue(option, index)"
              role="option"
              :aria-selected="isSelected(option)"
              :data-index="index"
              :class="[
                'text-base text-primary-text flex items-center h-8 p-1 rounded cursor-pointer transition-colors duration-200 whitespace-nowrap mb-1',
                isSelected(option) ? 'bg-blue/30' : '',
                focusedIndex === index ? 'hover:bg-blue/30' : '',
              ]"
              @click="selectOption(option)"
              @mouseenter="focusedIndex = index"
            >
              <img
                :class="['inline-block w-[14px] h-[9px] mr-[11px] ml-2 shrink-0 bg-no-repeat bg-center bg-contain', isSelected(option) ? 'visible' : 'invisible']"
                :src="require('../assets/img/tick.svg')"
              />

              <img v-if="optionImage" :src="optionImage(option)" :alt="getOptionLabel(option)" class="w-5 h-[14px] object-cover rounded-[2px] ltr:mr-2 rtl:ml-2 shrink-0" />
              <span class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{{ getOptionLabel(option) }}</span>
            </div>
          </template>

          <div v-else class="text-blue text-center py-3 px-2 text-sm">
            {{ resolvedNoResultsText }}
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'SearchableSelect',

  // Sharing the parent's $validator instance is what makes errors.has(name)
  // work in the parent component. Without this, v-validate on the hidden input
  // would register the field on a separate child validator and the parent
  // would never see it.
  inject: ['$validator'],

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: {
      default: null,
    },

    /**
     * Array of options.
     * Primitives: ['93', '355', '44']
     * Objects:    [{ label: '+93 Afghanistan', value: '93', countryCode: 'AF' }]
     */
    options: {
      type: Array,
      default: function () {
        return []
      },
    },

    /** Key used as the displayed label when options are objects. */
    labelKey: {
      type: String,
      default: null,
    },

    /** Key whose value is emitted by v-model when options are objects. */
    valueKey: {
      type: String,
      default: null,
    },

    /**
     * Key used as the :key in v-for kept separate from valueKey so that
     * options sharing the same value (e.g. multiple countries with dialCode "1")
     * don't trigger Vue's duplicate-key warning.
     * Falls back to the array index when not set.
     */
    trackBy: {
      type: String,
      default: null,
    },

    /** Text shown in the trigger when nothing is selected. */
    placeholder: {
      type: String,
      default: null,
    },

    /** Placeholder text inside the search field. */
    searchPlaceholder: {
      type: String,
      default: null,
    },

    /** Message shown when no option matches the typed query. */
    noResultsText: {
      type: String,
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    /** vee-validate field name  same as the name attribute on a native select. */
    name: {
      type: String,
      default: null,
    },

    /**
     * vee-validate rules in the same format as the v-validate directive.
     * @example rules="required"
     * @example :rules="{ required: true }"
     */
    rules: {
      type: [String, Object],
      default: null,
    },

    /** Human-readable field label for vee-validate error messages (data-vv-as). */
    dataVvAs: {
      type: String,
      default: null,
    },

    /**
     * vee-validate scope lets you validate a group of fields at once.
     * Useful in multi-step forms:
     *   this.$validator.validateAll('step1')
     *   this.$validator.validateAll('step2')
     */
    scope: {
      type: String,
      default: null,
    },

    /**
     * Custom search filter. Replaces the default label.includes(query) logic.
     * Useful when you want to search across multiple fields or need
     * accent-insensitive matching.
     * Signature: (option, query) => boolean
     *
     * Example  search by label AND country code:
     *   :filter-fn="(opt, q) => opt.label.toLowerCase().includes(q) || opt.countryCode.toLowerCase().includes(q)"
     */
    filterFn: {
      type: Function,
      default: null,
    },

    /**
     * Returns an image URL to show beside each option in the list.
     */
    optionImage: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      isOpen: false,
      searchQuery: '',
      focusedIndex: -1,
      selectedKey: null,
    }
  },

  computed: {
    resolvedPlaceholder() {
      return this.placeholder || this.$t('select_option').tTitle()
    },

    resolvedSearchPlaceholder() {
      return this.searchPlaceholder || this.$t('search').tTitle()
    },

    resolvedNoResultsText() {
      return this.noResultsText || this.$t('no_records').tTitle()
    },

    displayValue() {
      if (this.value === null || this.value === undefined || this.value === '') return ''
      var found = this.options.find(o => this.getOptionValue(o) === this.value)
      return found ? '+' + this.getOptionValue(found) : this.value
    },

    filteredOptions() {
      var q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.options
      var self = this
      if (this.filterFn) {
        return this.options.filter(o => {
          return self.filterFn(o, q)
        })
      }
      return this.options.filter(o => {
        return String(self.getOptionLabel(o)).toLowerCase().indexOf(q) !== -1
      })
    },
  },

  watch: {
    filteredOptions() {
      this.focusedIndex = -1
    },
  },

  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside)
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  },

  methods: {
    getTrackByValue(option, index) {
      if (this.trackBy && option !== null && typeof option === 'object') {
        return option[this.trackBy]
      }
      return index
    },

    getOptionLabel(option) {
      if (this.labelKey && option !== null && typeof option === 'object') {
        return option[this.labelKey]
      }
      return option
    },

    getOptionValue(option) {
      if (this.valueKey && option !== null && typeof option === 'object') {
        return option[this.valueKey]
      }
      return option
    },

    isSelected(option) {
      if (this.trackBy) {
        return this.selectedKey !== null && this.getTrackByValue(option, null) === this.selectedKey
      }
      return this.getOptionValue(option) === this.value
    },

    toggleOpen() {
      if (this.disabled) return
      this.isOpen ? this.closeDropdown() : this.openDropdown()
    },

    openDropdown() {
      this.isOpen = true
      this.searchQuery = ''
      this.focusedIndex = -1
      var self = this
      this.$nextTick(() => {
        if (self.$refs.searchInput) self.$refs.searchInput.focus()
      })
    },

    closeDropdown() {
      this.isOpen = false
      this.searchQuery = ''
      this.focusedIndex = -1
    },

    selectOption(option) {
      this.selectedKey = option !== null ? this.getTrackByValue(option, null) : null
      var val = option !== null ? this.getOptionValue(option) : null
      this.$emit('input', val)
      this.$emit('change', val)
      this.closeDropdown()

      var self = this
      this.$nextTick(() => {
        var input = self.$refs.vvInput
        if (!input) return
        input.value = val !== null && val !== undefined ? String(val) : ''
        input.dispatchEvent(new Event('input', { bubbles: true }))
        input.dispatchEvent(new Event('change', { bubbles: true }))
      })
    },

    selectFirstFiltered() {
      if (!this.filteredOptions.length) return
      var idx = this.focusedIndex >= 0 ? this.focusedIndex : 0
      this.selectOption(this.filteredOptions[idx])
    },

    focusNextOption(dir) {
      var total = this.filteredOptions.length
      if (!total) return
      this.focusedIndex = (this.focusedIndex + dir + total) % total
      var self = this
      this.$nextTick(() => {
        var scroll = self.$refs.optionsScroll
        if (!scroll) return
        var el = scroll.querySelector('[data-index="' + self.focusedIndex + '"]')
        if (el) el.scrollIntoView({ block: 'nearest' })
      })
    },

    handleClickOutside(e) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.closeDropdown()
      }
    },

    setSelectedByTrackKey(key) {
      this.selectedKey = key
    },
  },
}
</script>

<style scoped>
/*  Picker panel  */
.picker-panel {
  scrollbar-width: thin;
  scrollbar-color: #2b63e3 transparent;
}

/*  Options scroll container  */
.picker-options-scroll {
  flex: 1;
  scrollbar-width: thin;
  scrollbar-color: #2b63e3 transparent;
}
</style>
